/* cspell:disable */
/**
 * @file CarteraMnToolbarData.tsx
 * @summary Keeps the hidden `<mntoolbardata>` element in sync with the current Givebacks member state
 *          so the Cartera browser extension can personalize UX and associate shopping trips.
 *
 * @remarks
 * - Cartera’s extension reads `<mntoolbardata>` **on page load**. We therefore ensure the element
 *   exists and is inserted as the **first child of `<body>`** for predictable reads.
 * - The attributes written here follow the Cartera/Givebacks spec:
 *   - `isanonymous` — `true` when logged out, `false` when logged in
 *   - `memidtoken` — EMIT (encrypted member identifier token). Empty/omitted when logged out
 *   - `brandid` — Cartera program/brand id (issued by Cartera)
 *   - `gbcause` — Cause name; use the literal `"multiple"` when several are selected; omit when none
 *   - `gbcauselogo` — URL-encoded logo URL. Must be empty/omitted when `gbcause === "multiple"`
 *   - `firstname` — User's first name
 *   - `lastname` — User's last name
 * - After each write we dispatch a `CustomEvent` named `mntoolbardata:updated` with the full payload
 *   for optional analytics/debugging.
 */
import { useEffect, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useGetUserCarteraMemberId, useListUserCauseSelections, useUserData } from 'core/Api';
import md5 from 'crypto-js/md5';

/**
 * URL-encode a logo URL for safe transmission.
 *
 * @param url - Raw or possibly empty logo URL
 * @returns A URL-encoded string, or an empty string when `url` is falsy
 */
function encodeLogo(url: string | undefined): string {
  return url ? encodeURIComponent(url) : '';
}

/**
 * Data shape mirrored to the `<mntoolbardata>` element.
 *
 * @property isanonymous - `true` when logged out
 * @property memidtoken - EMIT (encrypted member identifier token). Empty/omitted when logged out
 * @property brandid - Cartera brand/program id (issued by Cartera) - 441 for givebacks
 * @property gbcause - Selected cause name; use `"multiple"` if the user has more than one, omit when none
 * @property gbcauselogo - URL-encoded logo URL for the selected cause (may be empty or url-encoded logo URL)
 * @property firstname - User's first name from userData
 * @property lastname - User's last name from userData
 */
type MntoolbarDataPayload = {
  isanonymous: boolean;
  memidtoken?: string;
  brandid?: 441; // value 441 issued from Cartera for givebacks
  gbcause?: string;
  gbcauselogo?: string;
  useridhashed?: string;
  firstname?: string;
  lastname?: string;
};

/**
 * Ensure there is exactly one `<mntoolbardata>` element in the DOM.
 * Creates it (hidden) at the very start of `<body>` if it does not exist.
 *
 * @returns The ensured element, or `null` during SSR
 */
function ensureMntoolbarDataElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let element = document.querySelector<HTMLElement>('mntoolbardata');
  if (!element) {
    element = document.createElement('mntoolbardata');
    element.style.display = 'none';
    // Insert as the first child in <body> for predictable reads by the extension
    document.body.insertBefore(element, document.body.firstChild);
  }
  return element;
}

/**
 * Set or clear an attribute on the target element, only if the value changed.
 * This minimizes layout/paint work and keeps mutations idempotent.
 */
function setAttr(element: HTMLElement, name: string, value: string | undefined | null) {
  if (value === undefined || value === null || value === '') {
    if (element.hasAttribute(name)) element.removeAttribute(name);
    return;
  }
  const current = element.getAttribute(name);
  if (current !== value) element.setAttribute(name, value);
}

/**
 * Write the full payload to the `<mntoolbardata>` element using `setAttr` diffs.
 */
function writeToElement(element: HTMLElement, data: MntoolbarDataPayload) {
  setAttr(element, 'isanonymous', String(!!data.isanonymous));
  setAttr(element, 'memidtoken', data.memidtoken || '');
  setAttr(element, 'brandid', data.brandid !== undefined ? String(data.brandid) : '');
  setAttr(element, 'gbcause', data.gbcause || '');
  setAttr(element, 'gbcauselogo', data.gbcauselogo || '');
  setAttr(element, 'useridhashed', data.useridhashed || '');
  setAttr(element, 'firstname', data.firstname || '');
  setAttr(element, 'lastname', data.lastname || '');
}

/**
 * React hook that composes the current member state and mirrors it into `<mntoolbardata>`.
 *
 * @param queryOptions.debounceMs - Debounce window (ms) to collapse near-simultaneous writes (default: 50ms)
 *
 * @remarks
 * - Mount this hook once per page (the exported component below already does this).
 * - Handles the following cases for causes:
 *   - **No selection** → omit `gbcause` and `gbcauselogo`
 *   - **Multiple selections** → `gbcause = "multiple"`, `gbcauselogo = ''`
 *   - **Single selection** → `gbcause = name`, `gbcauselogo = encodeURIComponent(logoUrl)`
 * - Dispatches `mntoolbardata:updated` after each write with `{ detail: MntoolbarDataPayload }`.
 */
const useCarteraMnToolbarDataSync = ({
  queryOptions,
}: {
  queryOptions?: {
    debounceMs?: number;
  };
} = {}) => {
  const { isAuthenticated } = useAuthentication();
  const { data: causeSelections } = useListUserCauseSelections();
  const { data: memIdToken } = useGetUserCarteraMemberId();
  const { data: userData } = useUserData();

  const useridhashed = useMemo(() => {
    if (!userData?.uuid) return undefined;

    return md5(userData.uuid).toString();
  }, [userData?.uuid]);

  // Debounce writes to avoid rapid thrashing if queries resolve at slightly different times
  const debounce = queryOptions?.debounceMs ?? 50;

  useEffect(() => {
    let id: ReturnType<typeof setTimeout> | undefined = undefined;

    try {
      // On mount and on every relevant data change, ensure element exists and sync
      const element = ensureMntoolbarDataElement();
      if (!element) return;

      const maybeWrite = (payload: Partial<MntoolbarDataPayload>) => {
        const full: MntoolbarDataPayload = {
          isanonymous: !!payload.isanonymous,
          memidtoken: payload.memidtoken ?? '',
          brandid: 441,
          gbcause: payload.gbcause,
          gbcauselogo: payload.gbcauselogo,
          useridhashed: payload.useridhashed,
          firstname: payload.firstname,
          lastname: payload.lastname,
        };

        writeToElement(element, full);

        // try {
        //   element.dispatchEvent(new CustomEvent('mntoolbardata:updated', { detail: full }));
        // } catch {
        //   // no-op
        // }
      };

      const hasNoSelectedCauses = !causeSelections || causeSelections.length === 0;
      const hasMultipleCauses = !!causeSelections && causeSelections.length > 1;
      const hasSingleCause = !!causeSelections && causeSelections.length === 1;

      let gbcause: string | undefined;
      let gbcauselogo: string | undefined;

      if (hasNoSelectedCauses) {
        gbcause = undefined;
        gbcauselogo = undefined;
      } else if (hasMultipleCauses) {
        gbcause = 'multiple';
        gbcauselogo = ''; // Empty for multiple causes per spec
      } else if (hasSingleCause) {
        gbcause = causeSelections[0].cause.name;
        gbcauselogo = encodeLogo(causeSelections[0].cause.logo?.url);
      }

      const merged: Partial<MntoolbarDataPayload> = {
        isanonymous: !isAuthenticated,
        gbcause,
        gbcauselogo,
        memidtoken: memIdToken ?? '',
        useridhashed,
        firstname: userData?.first_name,
        lastname: userData?.last_name,
      };

      id = setTimeout(() => maybeWrite(merged), debounce);
    } catch (error) {
      console.error(error);
    }

    return () => clearTimeout(id);
  }, [isAuthenticated, causeSelections, debounce, memIdToken, useridhashed, userData]);
};

/**
 * UI-less component that mounts the Cartera toolbar data sync behavior.
 *
 * @example
 * ```tsx
 * // In your app root (ensure QueryClientProvider is set up):
 * <CarteraMnToolbarData />
 * ```
 */
const CarteraMnToolbarDataClient = () => {
  // fetch data to sync to the mntoolbardata element
  useCarteraMnToolbarDataSync();
  return null;
};

export { CarteraMnToolbarDataClient };
